import { useTranslation } from 'react-i18next';
import { ScrollText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LegalLayout } from '../components/LegalLayout';

const CONTENT = {
  fr: {
    title: "Conditions d'utilisation",
    updated: "Dernière mise à jour : janvier 2026",
    sections: [
      {
        title: "1. Objet du service",
        body: "ARIA (Analyse Radiologique par Intelligence Artificielle) est un outil d'aide à l'interprétation d'images médicales destiné aux professionnels de santé. Il fournit des analyses générées par intelligence artificielle à titre d'assistance et de second avis, ainsi que des fonctions de gestion (historique, rapports PDF, statistiques).",
      },
      {
        title: "2. Avertissement médical",
        body: "ARIA ne constitue pas un dispositif de diagnostic autonome et ne remplace en aucun cas l'évaluation d'un radiologue ou d'un médecin qualifié. Les résultats produits peuvent comporter des erreurs, des omissions ou des incertitudes. Toute décision clinique relève exclusivement de la responsabilité du professionnel de santé utilisateur. En cas de suspicion d'urgence vitale, agissez immédiatement selon les protocoles en vigueur, sans attendre les résultats de l'outil.",
      },
      {
        title: "3. Accès et compte",
        body: "L'accès au service nécessite un compte praticien. Vous êtes responsable de la confidentialité de vos identifiants et de toute activité réalisée depuis votre compte. Vous vous engagez à fournir des informations exactes et à nous signaler sans délai toute utilisation non autorisée.",
      },
      {
        title: "4. Obligations de l'utilisateur",
        body: "Vous vous engagez à : (a) utiliser le service dans un cadre professionnel et conformément à la réglementation applicable, notamment en matière de données de santé ; (b) ne téléverser que des images et informations que vous êtes autorisé à traiter ; (c) anonymiser ou pseudonymiser les données patient lorsque cela est requis ; (d) ne pas tenter de contourner les mesures de sécurité du service.",
      },
      {
        title: "5. Propriété intellectuelle",
        body: "Le service, sa marque ARIA, son logo, son interface et ses contenus éditoriaux sont protégés. Vous conservez vos droits sur les images et données que vous téléversez ; vous nous accordez uniquement la licence technique nécessaire à leur traitement pour fournir le service.",
      },
      {
        title: "6. Disponibilité et responsabilité",
        body: "Le service est fourni « en l'état », sans garantie de disponibilité continue ni d'exactitude des résultats. Dans les limites permises par la loi, OpenMeta ne saurait être tenue responsable des dommages indirects résultant de l'utilisation ou de l'impossibilité d'utiliser le service, ni des décisions cliniques prises sur la base des analyses fournies.",
      },
      {
        title: "7. Modifications",
        body: "Nous pouvons faire évoluer le service et les présentes conditions. En cas de modification substantielle, vous en serez informé via l'application. La poursuite de l'utilisation vaut acceptation des conditions mises à jour.",
      },
      {
        title: "8. Contact",
        body: "Pour toute question relative aux présentes conditions : support@openmeta.fr.",
      },
    ],
  },
  en: {
    title: "Terms of Use",
    updated: "Last updated: January 2026",
    sections: [
      {
        title: "1. Purpose of the service",
        body: "ARIA (AI-Powered Radiological Analysis) is a medical image interpretation aid intended for healthcare professionals. It provides AI-generated analyses for assistance and second-opinion purposes, along with management features (history, PDF reports, statistics).",
      },
      {
        title: "2. Medical disclaimer",
        body: "ARIA is not a standalone diagnostic device and never replaces the assessment of a qualified radiologist or physician. Its outputs may contain errors, omissions or uncertainty. All clinical decisions remain the sole responsibility of the healthcare professional using the service. If a life-threatening emergency is suspected, act immediately according to applicable protocols without waiting for the tool's results.",
      },
      {
        title: "3. Access and account",
        body: "Access to the service requires a practitioner account. You are responsible for keeping your credentials confidential and for all activity performed from your account. You agree to provide accurate information and to notify us without delay of any unauthorized use.",
      },
      {
        title: "4. User obligations",
        body: "You agree to: (a) use the service in a professional context and in compliance with applicable regulations, in particular regarding health data; (b) upload only images and information you are authorized to process; (c) anonymize or pseudonymize patient data where required; (d) not attempt to circumvent the service's security measures.",
      },
      {
        title: "5. Intellectual property",
        body: "The service, the ARIA brand, its logo, interface and editorial content are protected. You retain your rights over the images and data you upload; you grant us only the technical license required to process them in order to provide the service.",
      },
      {
        title: "6. Availability and liability",
        body: "The service is provided \u201cas is\u201d, with no guarantee of continuous availability or accuracy of results. To the extent permitted by law, OpenMeta shall not be liable for indirect damages arising from the use or inability to use the service, nor for clinical decisions made on the basis of the analyses provided.",
      },
      {
        title: "7. Changes",
        body: "We may update the service and these terms. In the event of a substantial change, you will be informed through the application. Continued use constitutes acceptance of the updated terms.",
      },
      {
        title: "8. Contact",
        body: "For any question regarding these terms: support@openmeta.fr.",
      },
    ],
  },
};

export default function TermsPage() {
  const { i18n } = useTranslation();
  const c = CONTENT[i18n.language === 'en' ? 'en' : 'fr'];

  return (
    <LegalLayout>
      <div data-testid="terms-page">
        <div className="flex items-center gap-3 mb-1">
          <ScrollText size={26} className="text-cyan-400" />
          <h1 className="text-2xl font-bold text-foreground">{c.title}</h1>
        </div>
        <p className="text-xs text-muted-foreground mb-8">{c.updated}</p>

        <Card className="bg-[hsl(210,18%,9%)] border-border">
          <CardContent className="py-6 space-y-6">
            {c.sections.map((s, i) => (
              <section key={i} data-testid={`terms-section-${i}`}>
                <h2 className="text-sm font-semibold text-foreground mb-1.5">{s.title}</h2>
                <p className="text-sm text-foreground/75 leading-relaxed">{s.body}</p>
              </section>
            ))}
          </CardContent>
        </Card>
      </div>
    </LegalLayout>
  );
}
